import { getBoundsCenter } from './bbox.ts'
import {
  isLatLngBounds,
  isPolygon,
  type GeometryType,
  type LatLng,
  type LatLngBounds,
} from './types.ts'

export function createCirclePolygon(center: LatLng, radius: number, numPoints = 64) {
  const radiusPoints: LatLng[] = []

  for (let i = 0; i <= numPoints; i++) {
    const angle = (i / numPoints) * 2 * Math.PI
    const lat = center.lat + (radius / 111_320) * Math.cos(angle)
    const lng =
      center.lng + (radius / (111_320 * Math.cos((center.lat * Math.PI) / 180))) * Math.sin(angle)

    radiusPoints.push({
      lat,
      lng,
    })
  }

  return radiusPoints
}

export function createRectanglePolygon(center: LatLng, area: number) {
  // Calculate width and height to achieve the desired area
  // Using a 1:1 aspect ratio for simplicity
  const side = Math.sqrt(area)
  const halfWidth = side / (2 * 111_320 * Math.cos((center.lat * Math.PI) / 180)) // Convert to degrees longitude
  const halfHeight = side / (2 * 111_320) // Convert to degrees latitude

  // Create rectangle corners in clockwise order
  const points: LatLng[] = [
    { lat: center.lat + halfHeight, lng: center.lng - halfWidth }, // Top left
    { lat: center.lat + halfHeight, lng: center.lng + halfWidth }, // Top right
    { lat: center.lat - halfHeight, lng: center.lng + halfWidth }, // Bottom right
    { lat: center.lat - halfHeight, lng: center.lng - halfWidth }, // Bottom left
    { lat: center.lat + halfHeight, lng: center.lng - halfWidth }, // Back to start to close polygon
  ]

  return points
}

export function polygonToBounds(polygon: LatLng[]) {
  if (!isPolygon(polygon)) {
    return null
  }

  const bounds: LatLngBounds = {
    north: polygon[0].lat,
    south: polygon[0].lat,
    east: polygon[0].lng,
    west: polygon[0].lng,
  }

  for (const point of polygon) {
    bounds.north = Math.max(bounds.north, point.lat)
    bounds.south = Math.min(bounds.south, point.lat)
    bounds.east = Math.max(bounds.east, point.lng)
    bounds.west = Math.min(bounds.west, point.lng)
  }

  return bounds
}

export function getPolygonCenter(path: LatLng[]) {
  if (path.length === 0) {
    return undefined
  }

  let minLat = path[0].lat
  let maxLat = path[0].lat
  let minLng = path[0].lng
  let maxLng = path[0].lng

  for (const point of path) {
    minLat = Math.min(minLat, point.lat)
    maxLat = Math.max(maxLat, point.lat)
    minLng = Math.min(minLng, point.lng)
    maxLng = Math.max(maxLng, point.lng)
  }

  return {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2,
  }
}

export function getGeomLocation(geom: GeometryType | null | undefined) {
  if (!geom || (!isPolygon(geom) && !isLatLngBounds(geom))) {
    return null
  }

  let center: LatLng | undefined

  if (isPolygon(geom)) {
    center = getPolygonCenter(geom)
  } else if (isLatLngBounds(geom)) {
    center = getBoundsCenter(geom)
  } else {
    center = geom as LatLng
  }

  if (!center) {
    return null
  }

  return center
}
